import { useState, type ReactNode } from 'react';
import { T, useGT, Var } from 'gt-react';
import type { APInvoice } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';
import { useClearAPInvoiceSnoozes } from '@/api/rpc/hooks/accounts-payable/useSnoozeAPInvoices';
import { SnoozeInvoicesDialog } from './SnoozeInvoicesDialog';
import {
  formatSnoozedUntil,
  isActivelySnoozed,
  isSnoozableInvoiceStatus,
} from './invoiceSnooze';

type SnoozeMenuInvoice = {
  id: string;
  status: string;
  snoozedUntil?: Date | string | null;
};

/** "Snoozed until …" on the invoice. Snooze and clear live in the overflow menu. */
export function InvoiceSnoozeActions({ invoice }: { invoice: APInvoice }) {
  if (!isActivelySnoozed(invoice.snoozedUntil) || !invoice.snoozedUntil) {
    return null;
  }

  return (
    <span className="whitespace-nowrap text-xs text-muted-foreground">
      <T context="label on an invoice that is currently snoozed">
        Snoozed until <Var>{formatSnoozedUntil(invoice.snoozedUntil)}</Var>
      </T>
    </span>
  );
}

/** Snooze and clear for the slide-in overflow menu. */
export function useInvoiceSnoozeMenu(invoice: SnoozeMenuInvoice | null): {
  canSnooze: boolean;
  snoozed: boolean;
  clearPending: boolean;
  openDialog: () => void;
  clear: () => void;
  dialog: ReactNode;
} {
  const gt = useGT();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const clearSnooze = useClearAPInvoiceSnoozes();
  const canSnooze = invoice != null && isSnoozableInvoiceStatus(invoice.status);
  const snoozed = invoice != null && isActivelySnoozed(invoice.snoozedUntil);

  function clear() {
    if (invoice == null) return;
    void clearSnooze
      .mutateAsync({ invoiceIds: [invoice.id] })
      .then(() => {
        showToast({
          title: gt('Snooze cleared', {
            context: 'toast after clearing an invoice snooze',
          }),
          color: 'success',
        });
      })
      .catch((error: unknown) => {
        showToast({
          title: gt('Could not clear snooze', {
            context: 'toast when clearing an invoice snooze fails',
          }),
          message: error instanceof Error ? error.message : undefined,
          color: 'error',
        });
      });
  }

  return {
    canSnooze,
    snoozed,
    clearPending: clearSnooze.isPending,
    openDialog: () => setOpen(true),
    clear,
    dialog:
      invoice == null ? null : (
        <SnoozeInvoicesDialog
          invoiceIds={[invoice.id]}
          open={open}
          onOpenChange={setOpen}
        />
      ),
  };
}
